// src/components/a11y/AccessibilityWidget.js
// ═══════════════════════════════════════════════════════════════════════════
//   ABS END-TO-END PORTAL — Premium Accessibility Centre (WCAG 2.2)
//   One global widget for both the landing pages and the post-login app.
//   • Text scaling · readable font · line/letter spacing
//   • Contrast / invert / grayscale / low-saturation filters
//   • Highlight links & headings · big cursor · reading guide & mask
//   • Reduced motion · strong keyboard-focus indicator · skip-to-content
//   Settings persist in localStorage and apply on every page.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Drawer, IconButton, Tooltip, Box, Typography } from '@mui/material';
import { styled, keyframes, alpha } from '@mui/material/styles';
import { uiScale } from '../../theme/designTokens';

import AccessibilityNewRoundedIcon from '@mui/icons-material/AccessibilityNewRounded';
import CloseRoundedIcon          from '@mui/icons-material/CloseRounded';
import RestartAltRoundedIcon     from '@mui/icons-material/RestartAltRounded';
import TextIncreaseRoundedIcon   from '@mui/icons-material/TextIncreaseRounded';
import TextDecreaseRoundedIcon   from '@mui/icons-material/TextDecreaseRounded';
import FormatSizeRoundedIcon     from '@mui/icons-material/FormatSizeRounded';
import FontDownloadRoundedIcon   from '@mui/icons-material/FontDownloadRounded';
import FormatLineSpacingRoundedIcon from '@mui/icons-material/FormatLineSpacingRounded';
import SpaceBarRoundedIcon       from '@mui/icons-material/SpaceBarRounded';
import LinkRoundedIcon           from '@mui/icons-material/LinkRounded';
import TitleRoundedIcon          from '@mui/icons-material/TitleRounded';
import MouseRoundedIcon          from '@mui/icons-material/MouseRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import ViewDayRoundedIcon        from '@mui/icons-material/ViewDayRounded';
import ContrastRoundedIcon       from '@mui/icons-material/ContrastRounded';
import InvertColorsRoundedIcon   from '@mui/icons-material/InvertColorsRounded';
import FilterBAndWRoundedIcon    from '@mui/icons-material/FilterBAndWRounded';
import GradientRoundedIcon       from '@mui/icons-material/GradientRounded';
import KeyboardRoundedIcon       from '@mui/icons-material/KeyboardRounded';

/* ═══════════════════════════════════════════════
   TOKENS
═══════════════════════════════════════════════ */
const FONT_PRIMARY = `'Inter', system-ui, sans-serif`;
const FONT_DISPLAY = `'Poppins', system-ui, sans-serif`;
const T = {
  navy: '#0d3460', navyDeep: '#0a2540',
  primary: '#10B981', primaryDark: '#059669', accent: '#7dffbc',
  glowGreen: 'rgba(16,185,129,0.35)',
  border: '#E2E8F0', text: '#0F172A', textSoft: '#64748B',
};

const STORAGE_KEY = 'a11y-settings';
const APP_ROOT_ID = 'app-a11y-root';

/* `textScale` is what the USER chose, as a percentage of normal — so 100 always
   means "default", whatever the product is actually rendering at. It is not the
   applied zoom. The shipped size is `uiScale` (src/theme/designTokens.js) and
   the two multiply:

       zoom = (uiScale / 100) × (textScale / 100)

   Showing the raw applied figure was wrong: the panel read "92% — Default",
   which tells a user their text is shrunk when in fact they are exactly where
   the product intends. 92 is our implementation detail, not their setting.

   Storing it relative also means nothing needs re-basing when `uiScale` moves:
   100 keeps meaning default and the applied zoom follows on its own. */
const USER_BASE = 100;     // what "Default" reads as, and always will
const SETTINGS_VERSION = 3;
const TEXT_MIN = 70, TEXT_MAX = 160, TEXT_STEP = 10;   // all relative to USER_BASE
const clampScale = n => Math.min(TEXT_MAX, Math.max(TEXT_MIN, n));

/* The applied zoom for a given user percentage. */
const zoomFor = pct => +((uiScale / 100) * (pct / 100)).toFixed(4);

const DEFAULTS = {
  v: SETTINGS_VERSION,
  textScale: USER_BASE,
  readable: false,
  lineHeight: false,
  letterSpacing: false,
  links: false,
  titles: false,
  bigCursor: false,
  reduceMotion: false,
  focus: false,
  readingGuide: false,
  readingMask: false,
  filter: 'none', // none | high-contrast | invert | grayscale | low-saturation
};

/* Count of non-default settings (for the header badge). `v` is the schema
   version, not a user setting, so it never counts. */
function countActive(s) {
  return Object.entries(s).filter(([k, val]) =>
    (k === 'v' ? false
      : k === 'textScale' ? val !== USER_BASE
      : k === 'filter' ? val !== 'none'
      : val === true)).length;
}

/* ═══════════════════════════════════════════════
   INJECTED GLOBAL STYLESHEET
═══════════════════════════════════════════════ */
const A11Y_CSS = `
/* ── Visual filters (scoped to the app wrapper, never the widget) ── */
#${APP_ROOT_ID}.a11y-f-high-contrast   { filter: contrast(1.45) !important; }
#${APP_ROOT_ID}.a11y-f-invert          { filter: invert(1) hue-rotate(180deg) !important; }
#${APP_ROOT_ID}.a11y-f-invert img,
#${APP_ROOT_ID}.a11y-f-invert video    { filter: invert(1) hue-rotate(180deg) !important; }
#${APP_ROOT_ID}.a11y-f-grayscale       { filter: grayscale(1) !important; }
#${APP_ROOT_ID}.a11y-f-low-saturation  { filter: saturate(0.45) !important; }

/* ── Readable / legible font ── */
html.a11y-readable body,
html.a11y-readable p, html.a11y-readable span, html.a11y-readable div,
html.a11y-readable a, html.a11y-readable li, html.a11y-readable td,
html.a11y-readable th, html.a11y-readable label, html.a11y-readable button,
html.a11y-readable input, html.a11y-readable h1, html.a11y-readable h2,
html.a11y-readable h3, html.a11y-readable h4, html.a11y-readable h5, html.a11y-readable h6 {
  font-family: 'Atkinson Hyperlegible', system-ui, sans-serif !important;
}

/* ── Line height ── */
html.a11y-line-height p, html.a11y-line-height li, html.a11y-line-height span,
html.a11y-line-height div, html.a11y-line-height a, html.a11y-line-height td,
html.a11y-line-height th, html.a11y-line-height label {
  line-height: 2 !important;
}

/* ── Letter / word spacing ── */
html.a11y-letter-spacing p, html.a11y-letter-spacing li, html.a11y-letter-spacing span,
html.a11y-letter-spacing a, html.a11y-letter-spacing h1, html.a11y-letter-spacing h2,
html.a11y-letter-spacing h3, html.a11y-letter-spacing h4, html.a11y-letter-spacing button {
  letter-spacing: 0.1em !important; word-spacing: 0.16em !important;
}

/* ── Highlight links ── */
html.a11y-links a {
  text-decoration: underline !important;
  text-underline-offset: 3px !important;
  outline: 2px solid #ffae00 !important; outline-offset: 2px !important;
  background: rgba(255,174,0,0.16) !important; border-radius: 3px !important;
}

/* ── Highlight headings ── */
html.a11y-titles h1, html.a11y-titles h2, html.a11y-titles h3,
html.a11y-titles h4, html.a11y-titles h5, html.a11y-titles h6 {
  outline: 2px dashed #10B981 !important; outline-offset: 3px !important;
  background: rgba(16,185,129,0.07) !important; border-radius: 3px !important;
}

/* ── Big cursor ── */
html.a11y-cursor, html.a11y-cursor * {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='42' height='42' viewBox='0 0 24 24'%3E%3Cpath fill='%230d3460' stroke='%23ffffff' stroke-width='1.4' stroke-linejoin='round' d='M4 2l13 8.5-5.7 1.1 3.6 6.6-2.9 1.6-3.6-6.7L4 18.4z'/%3E%3C/svg%3E") 4 2, auto !important;
}

/* ── Reduced motion ── */
html.a11y-reduce-motion *, html.a11y-reduce-motion *::before, html.a11y-reduce-motion *::after {
  animation-duration: 0.001ms !important; animation-iteration-count: 1 !important;
  transition-duration: 0.001ms !important; scroll-behavior: auto !important;
}

/* ── Strong keyboard-focus indicator (WCAG 2.2 focus appearance) ── */
html.a11y-focus a:focus, html.a11y-focus button:focus, html.a11y-focus input:focus,
html.a11y-focus select:focus, html.a11y-focus textarea:focus, html.a11y-focus [tabindex]:focus,
html.a11y-focus a:focus-visible, html.a11y-focus button:focus-visible,
html.a11y-focus [tabindex]:focus-visible {
  outline: 3px solid #1565c0 !important; outline-offset: 3px !important;
  box-shadow: 0 0 0 6px rgba(21,101,192,0.32) !important; border-radius: 4px !important;
}
`;

function injectStyles() {
  if (typeof document === 'undefined') return;
  let el = document.getElementById('a11y-global-styles');
  if (!el) {
    el = document.createElement('style');
    el.id = 'a11y-global-styles';
    el.textContent = A11Y_CSS;
    document.head.appendChild(el);
  }
}

/* Load the legible (dyslexia-friendly) font on demand — keeps it off the
   critical path so it never slows down a normal page load. */
function loadReadableFont() {
  if (document.getElementById('a11y-readable-font')) return;
  const l = document.createElement('link');
  l.id = 'a11y-readable-font';
  l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:wght@400;700&display=swap';
  document.head.appendChild(l);
}

/* Text/UI zoom — applied to #app-a11y-root, NOT <html>.
   <html>-level zoom was tried first and measured broken: every MUI
   Tooltip/Popover/Menu/Drawer renders through a React portal into
   document.body, which sits OUTSIDE any element you zoom — but their
   position is computed from the anchor's getBoundingClientRect(), which is
   already expressed in final, post-zoom viewport pixels. Re-applying that
   already-zoomed number as a CSS length *inside* the zoomed <html> subtree
   scales it a second time, so every tooltip drifted from its anchor by an
   amount proportional to (zoom - 1) -- confirmed empirically: at 150% zoom
   tooltips landed ~390px away from their trigger button, and the error
   flipped direction below 100% zoom, growing either way from the anchor.
   #app-a11y-root wraps only the ROUTED APP CONTENT (see App.js) -- not the
   AccessibilityWidget itself, and not any portal target, since MUI portals
   and this widget's own Drawer/reading-guide overlays all mount on
   document.body, a sibling of #app-a11y-root. So: elements inside the
   wrapper (headers, sidebars, page content, the buttons that trigger
   tooltips) scale together and stay internally consistent -- a fixed
   header's rendered height and a fixed sidebar's hardcoded top offset are
   both plain numbers scaled by the same wrapper zoom, so they stay flush
   (verified). Anything portaled to body (tooltips, popovers, menus, this
   widget's own panel/reading-guide) is never inside the zoomed subtree, so
   it never needs a getBoundingClientRect() -> CSS-length round trip through
   a zoomed coordinate space, and can never drift. */
function applyTextScale(pct) {
  const root = document.getElementById(APP_ROOT_ID);
  /* Always an explicit value, never '' — the pre-paint baseline laid down by
     bootstrapTextScale() is a stylesheet rule on this same element, so clearing
     the inline style would drop back to that rule instead of to zoom:1. */
  if (root) root.style.zoom = String(zoomFor(pct));
  // Clear any legacy <html>-level zoom from the earlier (broken) approach.
  if (document.documentElement.style.zoom) document.documentElement.style.zoom = '';
  const legacy = document.getElementById('fs-style');
  if (legacy) legacy.textContent = '';
  try { localStorage.setItem('fontScale', String(pct)); } catch {}
}

/* The user's chosen percentage (100 = default), from whatever is persisted.
   Single source of truth — both the pre-paint bootstrap and the widget's own
   load path go through here so they can never disagree. */
export function readPersistedScale() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && typeof saved === 'object') {
      const n = Number(saved.textScale);
      if (!isFinite(n) || n <= 0) return USER_BASE;
      if (Number(saved.v) === SETTINGS_VERSION) return clampScale(n);
      /* Pre-v3 this field held the APPLIED percentage, measured against
         whatever baseline shipped at the time (`base`, or 100 before that
         existed). Convert to the user-relative form, which lands anyone who
         was simply on the default squarely on 100. */
      const oldBase = Number(saved.base) || 100;
      return clampScale(Math.round((n / oldBase) * 100));
    }
    /* The standalone legacy key predates `base` entirely, so it was always
       measured against 100 — already the relative form. */
    const fs = Number(localStorage.getItem('fontScale'));
    if (isFinite(fs) && fs > 0) return clampScale(Math.round(fs));
  } catch {}
  return USER_BASE;
}

/* Lay the resolved scale down as a stylesheet rule BEFORE React's first paint
   (called from index.js). Without it every page would paint once unscaled and
   then visibly snap when the widget's mount effect runs — a flash on every
   single page load, since the shipped scale is not 100%.
   applyTextScale() sets an inline `zoom` on the same element afterwards, which
   always wins over this rule. */
export function bootstrapTextScale() {
  if (typeof document === 'undefined') return;
  let el = document.getElementById('a11y-scale-boot');
  if (!el) {
    el = document.createElement('style');
    el.id = 'a11y-scale-boot';
    document.head.appendChild(el);
  }
  el.textContent = `#${APP_ROOT_ID}{zoom:${zoomFor(readPersistedScale())};}`;
}

/* ═══════════════════════════════════════════════
   ANIMATIONS
═══════════════════════════════════════════════ */
const popIn = keyframes`from{opacity:0;transform:translateY(8px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}`;
const dotPulse = keyframes`0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.7)}`;
const headerShine = keyframes`0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}`;

/* ═══════════════════════════════════════════════
   STYLED
═══════════════════════════════════════════════ */
/* Premium header trigger — designed for the navy gov bar */
const TriggerBtn = styled('button')({
  position: 'relative', appearance: 'none', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 7,
  height: 28, padding: '0 12px', borderRadius: 8,
  fontFamily: FONT_PRIMARY, fontSize: 11.5, fontWeight: 600,
  color: 'rgba(255,255,255,0.88)',
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.18)',
  transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
  '& .a11y-ico': { display: 'inline-flex', transition: 'transform .25s ease' },
  '& .a11y-dot': {
    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
    background: '#7dffbc', boxShadow: '0 0 6px rgba(125,255,188,0.9)',
    animation: `${dotPulse} 2.2s ease-in-out infinite`,
  },
  '&:hover': {
    color: '#fff',
    background: 'rgba(125,255,188,0.18)',
    borderColor: 'rgba(125,255,188,0.5)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 14px rgba(16,185,129,0.28)',
  },
  '&:hover .a11y-ico': { transform: 'scale(1.12) rotate(-6deg)' },
  '&:focus-visible': { outline: '2px solid #7dffbc', outlineOffset: 2 },
  /* Icon-only on smaller screens */
  '@media (max-width:768px)': {
    padding: '0 8px', gap: 6,
    '& .a11y-label': { display: 'none' },
  },
});

const TriggerBadge = styled('span')({
  position: 'absolute', top: -6, right: -6,
  minWidth: 16, height: 16, padding: '0 3px', borderRadius: 99,
  background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`,
  color: '#fff', fontFamily: FONT_PRIMARY, fontSize: 9.5, fontWeight: 800,
  display: 'grid', placeItems: 'center',
  border: '1.5px solid #0d3460',
  boxShadow: '0 2px 6px rgba(16,185,129,0.5)',
});

/* Exported trigger — drop into any header. Opens the global widget. */
export const A11yTrigger = memo(function A11yTrigger({ compact = false }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const onCount = e => setCount(e.detail || 0);
    window.addEventListener('a11y:count', onCount);
    window.dispatchEvent(new CustomEvent('a11y:request-count'));
    return () => window.removeEventListener('a11y:count', onCount);
  }, []);
  const open = () => window.dispatchEvent(new CustomEvent('a11y:open'));
  return (
    <Tooltip title="Accessibility options">
      <TriggerBtn onClick={open} aria-label="Open accessibility options" aria-haspopup="dialog">
        <span className="a11y-ico"><AccessibilityNewRoundedIcon sx={{ fontSize: 16 }} /></span>
        {!compact && <span className="a11y-label">Accessibility</span>}
        {count > 0 && <TriggerBadge>{count}</TriggerBadge>}
      </TriggerBtn>
    </Tooltip>
  );
});

const PanelHead = styled('div')({
  position: 'relative', padding: '16px 20px 16px', flexShrink: 0,
  color: '#fff', overflow: 'hidden',
  background: `linear-gradient(120deg, ${T.navyDeep} 0%, ${T.navy} 42%, #124d88 100%)`,
  backgroundSize: '220% 220%',
  animation: `${headerShine} 16s ease infinite`,
  /* aurora glow */
  '&::before': {
    content: '""', position: 'absolute', top: -70, right: -50, width: 200, height: 200,
    background: 'radial-gradient(circle, rgba(16,185,129,0.40), transparent 65%)',
    pointerEvents: 'none',
  },
  /* animated green accent line */
  '&::after': {
    content: '""', position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
    background: `linear-gradient(90deg, ${T.primaryDark}, ${T.primary} 50%, ${T.primaryDark})`,
  },
});

const ActivePill = styled('span')({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  height: 20, padding: '0 9px', borderRadius: 99,
  background: 'rgba(16,185,129,0.22)', border: '1px solid rgba(125,255,188,0.45)',
  fontFamily: FONT_PRIMARY, fontSize: 10.5, fontWeight: 700, color: '#d6ffe9',
  letterSpacing: '0.03em',
});

const GroupLabel = styled(Typography)({
  fontFamily: FONT_PRIMARY, fontSize: 11, fontWeight: 700,
  letterSpacing: '0.12em', textTransform: 'uppercase', color: T.textSoft,
  margin: '18px 4px 10px', display: 'flex', alignItems: 'center', gap: 8,
  '&::after': { content: '""', flex: 1, height: 1, background: T.border },
});

const Card = styled('button', { shouldForwardProp: p => p !== 'on' })(({ on }) => ({
  appearance: 'none', cursor: 'pointer', textAlign: 'left',
  display: 'flex', flexDirection: 'column', gap: 7, padding: '13px 13px',
  borderRadius: 14, minHeight: 78,
  fontFamily: FONT_PRIMARY,
  border: on ? `1.5px solid ${T.primary}` : `1.5px solid ${T.border}`,
  background: on
    ? 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(16,185,129,0.04))'
    : '#fff',
  boxShadow: on ? `0 4px 16px ${alpha(T.primary, 0.18)}` : '0 1px 2px rgba(15,23,42,0.04)',
  transition: 'all .2s cubic-bezier(0.16,1,0.3,1)',
  position: 'relative',
  animation: `${popIn} .3s ease both`,
  '&:hover': { transform: 'translateY(-2px)', borderColor: alpha(T.primary, 0.6), boxShadow: `0 6px 18px ${alpha(T.primary, 0.16)}` },
  '&:focus-visible': { outline: '3px solid #1565c0', outlineOffset: 2 },
  '& .ico': {
    width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center',
    color: on ? '#fff' : T.navy,
    background: on ? `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})` : alpha(T.navy, 0.07),
    transition: 'all .2s ease',
  },
  '& .ttl': { fontSize: 12.5, fontWeight: 600, color: T.text, lineHeight: 1.25 },
  '& .state': {
    position: 'absolute', top: 11, right: 12, fontSize: 9.5, fontWeight: 800,
    letterSpacing: '0.08em', color: on ? T.primaryDark : T.textSoft,
  },
}));

const ToggleCard = memo(({ icon, title, on, onClick }) => (
  <Card on={on ? 1 : 0} onClick={onClick} role="switch" aria-checked={on} aria-label={title}>
    <span className="state">{on ? 'ON' : 'OFF'}</span>
    <span className="ico">{icon}</span>
    <span className="ttl">{title}</span>
  </Card>
));

const FilterChip = styled('button', { shouldForwardProp: p => p !== 'on' })(({ on }) => ({
  appearance: 'none', cursor: 'pointer', flex: '1 1 auto',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '9px 8px', borderRadius: 11, fontFamily: FONT_PRIMARY,
  fontSize: 11.5, fontWeight: 700,
  border: on ? `1.5px solid ${T.primary}` : `1.5px solid ${T.border}`,
  background: on ? `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})` : '#fff',
  color: on ? '#fff' : T.text,
  transition: 'all .2s ease',
  '&:hover': { borderColor: alpha(T.primary, 0.6), transform: 'translateY(-1px)' },
  '&:focus-visible': { outline: '3px solid #1565c0', outlineOffset: 2 },
}));

const Stepper = styled('div')({
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 8, padding: 8, borderRadius: 14, border: `1.5px solid ${T.border}`,
  background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(13,52,96,0.03))',
});
const StepBtn = styled('button')({
  appearance: 'none', cursor: 'pointer', width: 40, height: 40, borderRadius: 11,
  border: 'none', color: '#fff', display: 'grid', placeItems: 'center',
  background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`,
  boxShadow: `0 3px 10px ${T.glowGreen}`, transition: 'all .18s ease',
  '&:hover': { transform: 'translateY(-1px)' },
  '&:disabled': { opacity: 0.4, cursor: 'not-allowed', transform: 'none' },
  '&:focus-visible': { outline: '3px solid #1565c0', outlineOffset: 2 },
});

const SkipLink = styled('a')({
  position: 'fixed', top: -80, left: 16, zIndex: 2000,
  padding: '10px 18px', borderRadius: 10,
  background: T.navy, color: '#fff', fontFamily: FONT_PRIMARY,
  fontSize: 14, fontWeight: 600, textDecoration: 'none',
  boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
  transition: 'top .2s ease',
  '&:focus': { top: 16, outline: '3px solid #7dffbc' },
});

/* ═══════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════ */
const FILTERS = [
  { key: 'high-contrast',  label: 'Contrast',  icon: <ContrastRoundedIcon sx={{ fontSize: 16 }} /> },
  { key: 'invert',         label: 'Invert',    icon: <InvertColorsRoundedIcon sx={{ fontSize: 16 }} /> },
  { key: 'grayscale',      label: 'Grayscale', icon: <FilterBAndWRoundedIcon sx={{ fontSize: 16 }} /> },
  { key: 'low-saturation', label: 'Low Sat.',  icon: <GradientRoundedIcon sx={{ fontSize: 16 }} /> },
];

function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(DEFAULTS);
  const [live, setLive] = useState('');
  const guideRef = useRef(null);
  const maskTopRef = useRef(null);
  const maskBotRef = useRef(null);
  const rafRef = useRef(false);

  /* Inject stylesheet + load saved settings once */
  useEffect(() => {
    injectStyles();
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      const merged = saved && typeof saved === 'object' ? { ...DEFAULTS, ...saved } : DEFAULTS;
      // textScale comes from readPersistedScale() so it gets the same migration
      // the pre-paint bootstrap already applied; stamping `v` records that this
      // value is now stored in the user-relative form.
      const { base, ...rest } = merged;   // drop the pre-v3 bookkeeping field
      setS({ ...rest, v: SETTINGS_VERSION, textScale: readPersistedScale() });
    } catch {}
  }, []);

  /* Keep latest settings in a ref for event handlers */
  const sRef = useRef(s);
  sRef.current = s;

  /* Listen for header triggers (mounted once) */
  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onRequest = () => window.dispatchEvent(new CustomEvent('a11y:count', { detail: countActive(sRef.current) }));
    const onText = e => {
      const d = e.detail || {};
      setS(prev => {
        const v = d.reset ? USER_BASE
          : clampScale(prev.textScale + (d.delta || 0) * TEXT_STEP);
        return { ...prev, textScale: v };
      });
    };
    window.addEventListener('a11y:open', onOpen);
    window.addEventListener('a11y:request-count', onRequest);
    window.addEventListener('a11y:text', onText);
    return () => {
      window.removeEventListener('a11y:open', onOpen);
      window.removeEventListener('a11y:request-count', onRequest);
      window.removeEventListener('a11y:text', onText);
    };
  }, []);

  /* Apply settings to the DOM whenever they change */
  useEffect(() => {
    const html = document.documentElement;
    const appRoot = document.getElementById(APP_ROOT_ID);

    applyTextScale(s.textScale);

    if (s.readable) loadReadableFont();
    html.classList.toggle('a11y-readable',        s.readable);
    html.classList.toggle('a11y-line-height',      s.lineHeight);
    html.classList.toggle('a11y-letter-spacing',   s.letterSpacing);
    html.classList.toggle('a11y-links',            s.links);
    html.classList.toggle('a11y-titles',           s.titles);
    html.classList.toggle('a11y-cursor',           s.bigCursor);
    html.classList.toggle('a11y-reduce-motion',    s.reduceMotion);
    html.classList.toggle('a11y-focus',            s.focus);

    if (appRoot) {
      ['high-contrast', 'invert', 'grayscale', 'low-saturation'].forEach(f =>
        appRoot.classList.toggle(`a11y-f-${f}`, s.filter === f));
    }

    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
    window.dispatchEvent(new CustomEvent('a11y:count', { detail: countActive(s) }));
  }, [s]);

  /* Reading guide & mask — follow the pointer (rAF-throttled, no re-render) */
  useEffect(() => {
    if (!s.readingGuide && !s.readingMask) return;
    const onMove = e => {
      if (rafRef.current) return;
      rafRef.current = true;
      // These overlays render as siblings of #app-a11y-root (see App.js),
      // so the app's text-scale zoom (scoped to #app-a11y-root, not <html>
      // — see applyTextScale) never touches them. clientY already matches
      // their own coordinate space 1:1, no compensation needed.
      const y = e.clientY;
      requestAnimationFrame(() => {
        if (guideRef.current) guideRef.current.style.top = `${y}px`;
        if (maskTopRef.current) maskTopRef.current.style.height = `${Math.max(0, y - 70)}px`;
        if (maskBotRef.current) maskBotRef.current.style.top = `${y + 70}px`;
        rafRef.current = false;
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [s.readingGuide, s.readingMask]);

  const announce = useCallback(msg => { setLive(''); requestAnimationFrame(() => setLive(msg)); }, []);

  const toggle = useCallback((key, label) => setS(prev => {
    const next = { ...prev, [key]: !prev[key] };
    announce(`${label} ${next[key] ? 'enabled' : 'disabled'}`);
    return next;
  }), [announce]);

  const setFilter = useCallback((key) => setS(prev => {
    const next = prev.filter === key ? 'none' : key;
    announce(next === 'none' ? 'Colour filter removed' : `${key.replace('-', ' ')} filter enabled`);
    return { ...prev, filter: next };
  }), [announce]);

  const changeText = useCallback((dir) => setS(prev => {
    const v = clampScale(prev.textScale + dir * TEXT_STEP);
    announce(`Text size ${v} percent`);
    return { ...prev, textScale: v };
  }), [announce]);

  const reset = useCallback(() => {
    setS(DEFAULTS);
    announce('All accessibility settings reset');
  }, [announce]);

  const nActive = countActive(s);

  return (
    <>
      {/* Skip to main content (WCAG 2.4.1 bypass blocks) */}
      <SkipLink href="#main-content">
        <KeyboardRoundedIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
        Skip to main content
      </SkipLink>

      {/* Reading guide & mask overlays (outside the filtered app wrapper) */}
      {s.readingGuide && (
        <Box ref={guideRef} aria-hidden sx={{
          position: 'fixed', left: 0, right: 0, top: 0, height: 0, zIndex: 1350, pointerEvents: 'none',
          borderTop: '3px solid rgba(16,185,129,0.9)',
          boxShadow: '0 0 0 1px rgba(255,255,255,0.6), 0 6px 18px rgba(16,185,129,0.4)',
        }} />
      )}
      {s.readingMask && (
        <>
          <Box ref={maskTopRef} aria-hidden sx={{ position: 'fixed', left: 0, right: 0, top: 0, height: 'calc(50vh - 70px)', background: 'rgba(5,16,30,0.62)', zIndex: 1340, pointerEvents: 'none' }} />
          <Box ref={maskBotRef} aria-hidden sx={{ position: 'fixed', left: 0, right: 0, top: 'calc(50vh + 70px)', bottom: 0, background: 'rgba(5,16,30,0.62)', zIndex: 1340, pointerEvents: 'none' }} />
        </>
      )}

      {/* aria-live announcer */}
      <Box aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {live}
      </Box>

      {/* Panel */}
      <Drawer
        anchor="right" open={open} onClose={() => setOpen(false)}
        sx={{ zIndex: 1500 }}
        PaperProps={{
          role: 'dialog', 'aria-label': 'Accessibility options',
          sx: { width: { xs: '100%', sm: 392 }, maxWidth: '100vw', background: '#F8FAFC' },
        }}
      >
        <PanelHead>
          <IconButton onClick={() => setOpen(false)} aria-label="Close accessibility options" size="small"
            sx={{ position: 'absolute', top: '50%', right: 14, transform: 'translateY(-50%)', zIndex: 2, color: '#fff', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 2, transition: 'all .2s', '&:hover': { background: 'rgba(255,255,255,0.14)', transform: 'translateY(-50%) rotate(90deg)' } }}>
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Box sx={{ position: 'relative', zIndex: 1, pr: 5 }}>
            <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 18, lineHeight: 1.15, letterSpacing: '0.01em' }}>
              Accessibility Centre
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: .4, flexWrap: 'wrap' }}>
              <Typography sx={{ fontFamily: FONT_PRIMARY, fontSize: 11.5, color: 'rgba(255,255,255,0.62)', letterSpacing: '0.03em' }}>
                Personalise your experience
              </Typography>
              {nActive > 0 && (
                <ActivePill>
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', background: '#7dffbc', boxShadow: '0 0 6px rgba(125,255,188,0.9)' }} />
                  {nActive} active
                </ActivePill>
              )}
            </Box>
          </Box>
        </PanelHead>

        <Box sx={{ p: 2, overflowY: 'auto', flex: 1, minHeight: 0 }}>

          {/* Text size */}
          <GroupLabel><FormatSizeRoundedIcon sx={{ fontSize: 15 }} /> Text size</GroupLabel>
          <Stepper>
            <StepBtn onClick={() => changeText(-1)} disabled={s.textScale <= TEXT_MIN} aria-label="Decrease text size">
              <TextDecreaseRoundedIcon sx={{ fontSize: 20 }} />
            </StepBtn>
            <Box sx={{ textAlign: 'center', flex: 1, px: 1.2 }}>
              <Typography sx={{
                fontFamily: FONT_PRIMARY, fontWeight: 800, fontSize: 18, lineHeight: 1,
                color: s.textScale === USER_BASE ? T.navy : T.primaryDark,
                transition: 'color .25s ease',
              }}>{s.textScale}%</Typography>
              <Typography sx={{ fontFamily: FONT_PRIMARY, fontSize: 10.5, color: T.textSoft, mt: .3 }}>
                {s.textScale === USER_BASE ? 'Default' : s.textScale > USER_BASE ? 'Enlarged' : 'Reduced'}
              </Typography>
              {/* Position within the available range, with a tick at Default —
                  makes the headroom left in each direction visible at a glance,
                  which two arrow buttons alone never communicated. */}
              <Box aria-hidden sx={{ position: 'relative', mt: .9, height: 4, borderRadius: 99, background: 'rgba(13,52,96,0.10)' }}>
                <Box sx={{
                  position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 99,
                  width: `${((s.textScale - TEXT_MIN) / (TEXT_MAX - TEXT_MIN)) * 100}%`,
                  background: `linear-gradient(90deg, ${T.primary}, ${T.primaryDark})`,
                  boxShadow: `0 0 8px ${T.glowGreen}`,
                  transition: 'width .3s cubic-bezier(0.16,1,0.3,1)',
                }} />
                <Box sx={{
                  position: 'absolute', top: -2.5, height: 9, width: 2, borderRadius: 99,
                  left: `${((USER_BASE - TEXT_MIN) / (TEXT_MAX - TEXT_MIN)) * 100}%`,
                  transform: 'translateX(-50%)', background: T.navy, opacity: 0.32,
                }} />
              </Box>
            </Box>
            <StepBtn onClick={() => changeText(1)} disabled={s.textScale >= TEXT_MAX} aria-label="Increase text size">
              <TextIncreaseRoundedIcon sx={{ fontSize: 20 }} />
            </StepBtn>
          </Stepper>

          {/* Reading & text aids */}
          <GroupLabel>Reading &amp; text</GroupLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
            <ToggleCard icon={<FontDownloadRoundedIcon sx={{ fontSize: 18 }} />}   title="Readable font"   on={s.readable}      onClick={() => toggle('readable', 'Readable font')} />
            <ToggleCard icon={<FormatLineSpacingRoundedIcon sx={{ fontSize: 18 }} />} title="Line spacing"  on={s.lineHeight}    onClick={() => toggle('lineHeight', 'Line spacing')} />
            <ToggleCard icon={<SpaceBarRoundedIcon sx={{ fontSize: 18 }} />}       title="Letter spacing"  on={s.letterSpacing} onClick={() => toggle('letterSpacing', 'Letter spacing')} />
            <ToggleCard icon={<LinkRoundedIcon sx={{ fontSize: 18 }} />}           title="Highlight links" on={s.links}         onClick={() => toggle('links', 'Highlight links')} />
            <ToggleCard icon={<TitleRoundedIcon sx={{ fontSize: 18 }} />}          title="Highlight titles" on={s.titles}       onClick={() => toggle('titles', 'Highlight titles')} />
            <ToggleCard icon={<HorizontalRuleRoundedIcon sx={{ fontSize: 18 }} />} title="Reading guide"   on={s.readingGuide}  onClick={() => toggle('readingGuide', 'Reading guide')} />
            <ToggleCard icon={<ViewDayRoundedIcon sx={{ fontSize: 18 }} />}        title="Reading mask"    on={s.readingMask}   onClick={() => toggle('readingMask', 'Reading mask')} />
            <ToggleCard icon={<MouseRoundedIcon sx={{ fontSize: 18 }} />}          title="Big cursor"      on={s.bigCursor}     onClick={() => toggle('bigCursor', 'Big cursor')} />
            <ToggleCard icon={<CenterFocusStrongRoundedIcon sx={{ fontSize: 18 }} />} title="Focus outline" on={s.focus}       onClick={() => toggle('focus', 'Focus indicator')} />
          </Box>

          {/* Motion */}
          <GroupLabel>Motion</GroupLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
            <ToggleCard icon={<PauseCircleRoundedIcon sx={{ fontSize: 18 }} />} title="Pause animation" on={s.reduceMotion} onClick={() => toggle('reduceMotion', 'Reduced motion')} />
          </Box>

          {/* Colour & contrast */}
          <GroupLabel><ContrastRoundedIcon sx={{ fontSize: 15 }} /> Colour &amp; contrast</GroupLabel>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <FilterChip key={f.key} on={s.filter === f.key ? 1 : 0} onClick={() => setFilter(f.key)}
                role="switch" aria-checked={s.filter === f.key} aria-label={`${f.label} filter`}>
                {f.icon}{f.label}
              </FilterChip>
            ))}
          </Box>

          {/* Reset */}
          <Box
            component="button" onClick={reset} aria-label="Reset all accessibility settings"
            sx={{
              mt: 3, width: '100%', height: 46, borderRadius: 3, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
              fontFamily: FONT_PRIMARY, fontSize: 14, fontWeight: 700, color: '#B91C1C',
              background: 'rgba(220,38,38,0.06)', border: '1.5px solid rgba(220,38,38,0.25)',
              transition: 'all .2s ease',
              '&:hover': { background: 'rgba(220,38,38,0.12)', borderColor: 'rgba(220,38,38,0.45)' },
              '&:focus-visible': { outline: '3px solid #1565c0', outlineOffset: 2 },
            }}
          >
            <RestartAltRoundedIcon sx={{ fontSize: 19 }} /> Reset all
          </Box>

          <Typography sx={{ fontFamily: FONT_PRIMARY, fontSize: 10.5, color: T.textSoft, textAlign: 'center', mt: 2, mb: 1, lineHeight: 1.6 }}>
            Your preferences are saved on this device and applied across the portal.
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}

export default memo(AccessibilityWidget);
